import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "urql";
import { Formik, Form } from "formik";
import { sortBy } from "lodash";
import { Link, useLocation, useParams } from "wouter";
import * as Yup from "yup";

import { emptyObjectId } from "../../../../common/src/types";
import { CheckBoxField, DateField, SelectField, SubmitButton } from "../../../../components/src/form";
import { ReactTable } from "../../../../components/src/react-table/ReactTable";
import { createReactTableColumnHelper, ReactTableColumnDef } from "../../../../components/src/react-table/features";

import { ACCOUNTING_RUNS, UPSERT_ACCOUNTING_RUN } from "../../../../graphql/src/accountingrun2/queries";
import { T_ACCOUNT_CHARTS } from "../../../../graphql/src/taccountchart2/queries";

import { SelectDropdown } from "../../components/SelectDropdown";
import { AccountingRunStatusEnum, AccountingRunTypeEnum } from "../../types.generated";
import type { AccountingRun2, Party, UpsertAccountingRunInput, UpsertTAccountChart } from "../../types.generated";

const DEFAULT_T_ACCOUNT_CHART_NAME = "Default fund t-account chart";

const runSchema = Yup.object({
    endDate: Yup.string().required("End date is required"),
    type: Yup.string().required("Type is required"),
    clientTAccountChartId: Yup.string().required("T-account chart is required")
});

export function AccountingRunsPage(): React.ReactElement {
    const { clientId } = useParams<{ clientId?: string }>();
    const [_, navigate] = useLocation();
    const [{ fetching, error, data }] = useQuery<{ tAccountCharts2: UpsertTAccountChart[]; clients: Party[] }>({
        query: T_ACCOUNT_CHARTS,
        variables: { filter: clientId ? { clientIdIn: [clientId] } : null },
        requestPolicy: "network-only"
    });
    // deleted runs are left out. Always from the server, since runs are created and change status on other pages, so a client change
    // or coming back to the page shows them as when the page is reloaded
    const [{ fetching: fetchingRuns, error: runsError, data: runsData }] = useQuery<{ accountingRuns2: AccountingRun2[] }>({
        query: ACCOUNTING_RUNS,
        variables: {
            filter: {
                clientIdIn: [clientId],
                statusIn: [AccountingRunStatusEnum.Pending, AccountingRunStatusEnum.Preliminary, AccountingRunStatusEnum.Confirmed]
            }
        },
        requestPolicy: "network-only",
        pause: !clientId
    });
    const [_upsertState, upsertAccountingRun] = useMutation(UPSERT_ACCOUNTING_RUN);
    const [upsertError, setUpsertError] = useState<string | null>(null);
    // today in local time by default, a chosen end date is kept when the client changes
    const [endDate, setEndDate] = useState<string>(new Date().toLocaleDateString("sv-SE"));

    const clients = useMemo(() => {
        if (data && data.clients) {
            return sortBy(data.clients, "name");
        }
        return [];
    }, [data]);

    const runs = useMemo(() => {
        if (clientId && runsData && runsData.accountingRuns2) {
            return runsData.accountingRuns2;
        }
        return [];
    }, [clientId, runsData]);

    // a client has at most one Pending or Preliminary run and no run can be created while it exists. A Pending run is the only
    // one that can be updated, a Preliminary run must be confirmed or deleted first
    const pendingRun = runs.find((run) => run.status === AccountingRunStatusEnum.Pending);
    const preliminaryRun = runs.find((run) => run.status === AccountingRunStatusEnum.Preliminary);
    // the runs are sorted by number descending, a run is built on the last Confirmed run and must end after it
    const lastConfirmedRun = runs.find((run) => run.status === AccountingRunStatusEnum.Confirmed);

    const tAccountChartOptions = useMemo(() => {
        const options = [{ key: emptyObjectId, value: DEFAULT_T_ACCOUNT_CHART_NAME }];
        if (data && data.tAccountCharts2) {
            for (const tAccountChart of data.tAccountCharts2) {
                options.push({ key: tAccountChart._id, value: `${tAccountChart.name || "(no name)"} version ${tAccountChart.version}` });
            }
        }
        return options;
    }, [data]);

    const columns = useMemo<ReactTableColumnDef<AccountingRun2>[]>(() => {
        const columnHelper = createReactTableColumnHelper<AccountingRun2>();

        return [
            columnHelper.accessor("number", {
                header: "Number",
                cell: (cellProps) => <Link to={`/accountingruns2/${clientId}/${cellProps.row.original._id}`}>{cellProps.getValue()}</Link>,
                size: 75
            }),
            columnHelper.accessor("accountingPeriod", {
                header: "Period",
                size: 75
            }),
            columnHelper.accessor("endDate", {
                header: "End date",
                size: 100
            }),
            columnHelper.accessor("status", {
                header: "Status",
                size: 100
            }),
            columnHelper.accessor("type", {
                header: "Type",
                size: 100
            }),
            columnHelper.accessor(
                (run) =>
                    run.clientTAccountChart
                        ? `${run.clientTAccountChart.name || "(no name)"} version ${run.clientTAccountChart.version}`
                        : DEFAULT_T_ACCOUNT_CHART_NAME,
                {
                    id: "tAccountChart",
                    header: "T-account chart",
                    size: 250,
                    cell: (cellProps) =>
                        cellProps.row.original.clientTAccountChart ? (
                            <Link to={`/taccountcharts2/${cellProps.row.original.clientTAccountChart._id}`}>{cellProps.getValue()}</Link>
                        ) : (
                            cellProps.getValue()
                        )
                }
            ),
            columnHelper.accessor((run) => run.updateUserInfo?.name, {
                id: "updateUser",
                header: "Updated by",
                size: 150
            }),
            columnHelper.accessor("updateTimestamp", {
                header: "Update timestamp",
                size: 200
            }),
            columnHelper.display({
                id: "statements",
                header: "Statements",
                cell: (cellProps) => (
                    <Link
                        to={`/accountingruns2/${clientId}/${cellProps.row.original._id}/statements`}
                        title="Balance sheet and income statement"
                    >
                        Statements
                    </Link>
                ),
                size: 100
            })
        ];
    }, [clientId]);

    if (fetching) return <p>Loading</p>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;

    let initialValues = {
        endDate,
        type: AccountingRunTypeEnum.Transaction as string,
        clientTAccountChartId: emptyObjectId,
        keepManualJournalEntries: true
    };
    if (pendingRun) {
        initialValues = {
            endDate: pendingRun.endDate,
            type: pendingRun.type,
            clientTAccountChartId: pendingRun.clientTAccountChartId,
            keepManualJournalEntries: pendingRun.keepManualJournalEntries !== false
        };
    }

    let validationSchema = runSchema;
    if (lastConfirmedRun) {
        validationSchema = runSchema.shape({
            endDate: Yup.string()
                .required("End date is required")
                .test(
                    "after-last-confirmed-run",
                    `End date must be later than ${lastConfirmedRun.endDate}`,
                    (endDate) => endDate > lastConfirmedRun.endDate
                )
        });
    }

    let description = "The first run of the client.";
    if (pendingRun) {
        description = "The journal entries of the run are generated again.";
    } else if (preliminaryRun) {
        description = `Accounting run ${preliminaryRun.number} is Preliminary, confirm or delete it before a new run can be created.`;
    }
    if (lastConfirmedRun && !preliminaryRun) {
        const builtOn = `The run is built on confirmed run ${lastConfirmedRun.number} and must end after ${lastConfirmedRun.endDate}.`;
        // not the first run of the client
        description = pendingRun ? description + " " + builtOn : builtOn;
    }

    return (
        <div>
            <h4>Accounting runs</h4>
            <p>
                <Link to="/accountinglogic2">Accounting logic</Link>
            </p>
            <div className="mb-4 w-full md:w-1/3">
                <SelectDropdown
                    id="client"
                    placeholder="Select client"
                    className="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900"
                    defaultValue={clientId}
                    options={clients.map((client) => ({ key: client._id, text: client.name }))}
                    onChange={(option) => {
                        // an error of the previous client is not relevant anymore
                        setUpsertError(null);
                        navigate(`/accountingruns2/${option.key}`);
                    }}
                />
            </div>
            {clientId ? (
                <div className="flex flex-wrap -mx-2">
                    <div className="w-full lg:w-2/3 px-2 mb-4">
                        {fetchingRuns ? <p>Loading</p> : null}
                        {runsError ? <p>error: {JSON.stringify(runsError, null, 2)}</p> : null}
                        <p>Number of accounting runs: {runs.length}</p>
                        <ReactTable columns={columns} data={runs} />
                    </div>
                    <div className="w-full lg:w-1/3 px-2 mb-4">
                        <h5>{pendingRun ? `Update pending accounting run ${pendingRun.number}` : "Create accounting run"}</h5>
                        <p>{description}</p>
                        {preliminaryRun ? null : (
                            <Formik
                                // a new form when the client or the pending run changes
                                key={`${clientId}-${pendingRun ? pendingRun._id : "new"}`}
                                initialValues={initialValues}
                                validationSchema={validationSchema}
                                validate={(values) => {
                                    // the date picker sets the value without a change event, remembered here for the form of the next client
                                    if (values.endDate !== initialValues.endDate) setEndDate(values.endDate);
                                    return {};
                                }}
                                onSubmit={async (values) => {
                                    setUpsertError(null);
                                    const input: UpsertAccountingRunInput = {
                                        clientId,
                                        endDate: values.endDate,
                                        type: values.type as AccountingRunTypeEnum,
                                        clientTAccountChartId: values.clientTAccountChartId
                                    };
                                    if (pendingRun) {
                                        input._id = pendingRun._id;
                                        input.keepManualJournalEntries = values.keepManualJournalEntries;
                                    }
                                    const result = await upsertAccountingRun({ input });
                                    if (result.error) {
                                        setUpsertError(result.error.message);
                                    } else {
                                        // the run page shows the journal entries of the run
                                        navigate(`/accountingruns2/${clientId}/${result.data.upsertAccountingRun._id}`);
                                    }
                                }}
                            >
                                {({ isSubmitting }) => (
                                    <Form autoComplete="off">
                                        <DateField
                                            name="endDate"
                                            label="End date"
                                            // full width as the select fields below
                                            className="[&_.react-datepicker-wrapper]:w-full [&_input]:w-full"
                                            disabled={false}
                                        />
                                        <SelectField
                                            name="type"
                                            label="Type"
                                            className=""
                                            disabled={false}
                                            options={Object.values(AccountingRunTypeEnum)}
                                        />
                                        <SelectField
                                            name="clientTAccountChartId"
                                            label="T-account chart"
                                            className=""
                                            disabled={false}
                                            options={tAccountChartOptions}
                                        />
                                        {pendingRun ? (
                                            <CheckBoxField
                                                name="keepManualJournalEntries"
                                                label="Keep manual journal entries"
                                                className=""
                                                disabled={false}
                                            />
                                        ) : null}
                                        <SubmitButton disabled={isSubmitting} label={pendingRun ? "Update" : "Create"} />
                                        {upsertError ? <div className="form-field-error">{upsertError}</div> : null}
                                    </Form>
                                )}
                            </Formik>
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
